import { Component, DestroyRef, inject, output, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { AbstractControl, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ChargesService } from '../../../../core/services/charge/charge.service';
import {
    PropertyChargeScheduleModel,
    UpdatePropertyChargeScheduleRequest
} from '../../../../core/models/data/property-charge-schedule.model';
import { BalancesMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';
import { CustomValidators } from '../../../../core/validators/custom-validators';
import { DateUtilities } from '../../../../core/utilities/date.utilities';
import { ScheduleFrequency } from '../../../../core/enums/data.enums';
import { FormValidatorMessages } from '../../../../core/enums/forms.enums';
import { DatePlaceholderDirective } from '../../../../core/directives/date-placeholder.directive';

@Component({
    selector: 'ms-reactivate-invoice-schedule-modal',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        ConfirmationModalComponent,
        InfoModalComponent,
        EscCloseDirective,
        InputLabel,
        DatePlaceholderDirective
    ],
    templateUrl: './reactivate-invoice-schedule-modal.component.html'
})
export class ReactivateInvoiceScheduleModalComponent extends BaseFormComponent implements CanComponentDeactivate {

    //#region Properties & Signals

    public reactivateCompleted = output<PropertyChargeScheduleModel>();

    public schedule = signal<PropertyChargeScheduleModel | undefined>(undefined);
    public scheduleName = signal<string | undefined>(undefined);

    public BalancesMessagingConstants = BalancesMessagingConstants;

    public reactivateForm: FormGroup;

    // `endDate` is only offered for recurring schedules. A Once schedule has no meaningful end date,
    // so the checkbox/picker is hidden and `endDateEnabled` stays false.
    public showEndDateControls = signal<boolean>(false);

    @ViewChild('reactivateInvoiceScheduleInfoRef') infoRef!: InfoModalComponent;
    @ViewChild('reactivateInvoiceScheduleEscConfirmationRef') escConfirmationRef!: ConfirmationModalComponent;

    //#endregion Properties & Signals

    //#region Constructor

    private fb = inject(FormBuilder);
    private chargesService = inject(ChargesService);
    private bootstrapService = inject(BootstrapService);
    private destroyRef = inject(DestroyRef);
    private dirtyFormRegistry = inject(DirtyFormRegistryService);

    constructor() {
        super();

        this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

        this.reactivateForm = this.fb.group({
            issuedDate: ['', [Validators.required, CustomValidators.futureDate('Issued date cannot be in the past')]],
            dueDate: ['', [Validators.required, CustomValidators.futureDate('Due date cannot be in the past')]],
            endDateEnabled: [false],
            endDate: [{ value: '', disabled: true }, CustomValidators.futureDate('End date cannot be in the past')]
        }, {
            validators: this.reactivateDateRangeValidator()
        });

        // Enable/disable the endDate control alongside the checkbox so a disabled/blank picker
        // doesn't hold onto a past value that the group validator would then reject.
        this.reactivateForm.get('endDateEnabled')?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((enabled: boolean) => {
                const endDateControl = this.reactivateForm.get('endDate');
                if (!endDateControl) return;
                if (enabled) {
                    endDateControl.enable({ emitEvent: false });
                } else {
                    endDateControl.setValue('', { emitEvent: false });
                    endDateControl.disable({ emitEvent: false });
                }
                this.reactivateForm.updateValueAndValidity({ emitEvent: false });
            });
    }

    //#endregion Constructor

    //#region Public Methods

    public open(schedule: PropertyChargeScheduleModel): void {
        this.schedule.set(schedule);
        this.scheduleName.set(schedule.name);
        this.showEndDateControls.set(schedule.frequency !== ScheduleFrequency.Once);
        this.resetForm();
        this.dirtyFormRegistry.register(this);
        this.bootstrapService.manageModal('#reactivateInvoiceScheduleModal', ModalActions.Show);
    }

    //#endregion Public Methods

    //#region Form Submission

    public async onSubmit(): Promise<void> {
        if (this.reactivateForm.invalid) {
            this.reactivateForm.markAllAsTouched();
            return;
        }

        const schedule = this.schedule();
        if (!schedule) return;

        this.isSubmitting = true;

        try {
            // The schedule handed in from the list comes from the filter endpoint, which does not
            // populate propertyIds. Fetching with includeProperties=true here so the update request
            // preserves the schedule's attached properties instead of detaching all of them.
            const fullScheduleResponse = await this.chargesService.getPropertyChargeScheduleById(
                schedule.propertyChargeScheduleId,
                true
            );

            if (!fullScheduleResponse.success || !fullScheduleResponse.data) {
                this.bootstrapService.manageModal('#reactivateInvoiceScheduleModal', ModalActions.Hide);
                this.infoRef.modalType.set(ModalTypes.Failure);
                this.infoRef.message.set(
                    fullScheduleResponse.message || BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_REACTIVATE_FAILURE()
                );
                this.bootstrapService.manageModal('#reactivateInvoiceScheduleInfoModal', ModalActions.Show);
                return;
            }

            const request = this.buildRequest(fullScheduleResponse.data);
            const result = await this.chargesService.updatePropertyChargeSchedule(
                schedule.propertyChargeScheduleId,
                request
            );

            this.bootstrapService.manageModal('#reactivateInvoiceScheduleModal', ModalActions.Hide);

            if (result.success && result.data) {
                this.submitSucceeded = true;
                this.infoRef.modalType.set(ModalTypes.Success);
                this.infoRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_REACTIVATE_SUCCESS());
                this.bootstrapService.manageModal('#reactivateInvoiceScheduleInfoModal', ModalActions.Show);
                this.reactivateCompleted.emit(result.data);
            } else {
                this.infoRef.modalType.set(ModalTypes.Failure);
                this.infoRef.message.set(
                    result.message || BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_REACTIVATE_FAILURE()
                );
                this.bootstrapService.manageModal('#reactivateInvoiceScheduleInfoModal', ModalActions.Show);
            }
        } catch {
            this.bootstrapService.manageModal('#reactivateInvoiceScheduleModal', ModalActions.Hide);
            this.infoRef.modalType.set(ModalTypes.Failure);
            this.infoRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_REACTIVATE_FAILURE());
            this.bootstrapService.manageModal('#reactivateInvoiceScheduleInfoModal', ModalActions.Show);
        } finally {
            this.isSubmitting = false;
        }
    }

    //#endregion Form Submission

    //#region Modal Management

    public onCancel(): void {
        if (this.reactivateForm.dirty) {
            this.escConfirmationRef.modalType.set(ModalTypes.Question);
            this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
            this.bootstrapService.manageModal('#reactivateInvoiceScheduleModal', ModalActions.Hide);
            this.bootstrapService.manageModal('#reactivateInvoiceScheduleEscConfirmationModal', ModalActions.Show);
        } else {
            this.bootstrapService.manageModal('#reactivateInvoiceScheduleModal', ModalActions.Hide);
            this.dirtyFormRegistry.unregister(this);
        }
    }

    public infoModalConfirmed(): void {
        this.bootstrapService.manageModal('#reactivateInvoiceScheduleInfoModal', ModalActions.Hide);
        this.dirtyFormRegistry.unregister(this);
    }

    public canDeactivate(): boolean | Promise<boolean> {
        return this.awaitLeaveConfirmation(this.reactivateForm.dirty && !this.submitSucceeded, () => {
            this.escConfirmationRef.modalType.set(ModalTypes.Question);
            this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
            this.bootstrapService.manageModal('#reactivateInvoiceScheduleModal', ModalActions.Hide);
            this.bootstrapService.manageModal('#reactivateInvoiceScheduleEscConfirmationModal', ModalActions.Show);
        });
    }

    public onEscConfirmed(): void {
        this.bootstrapService.manageModal('#reactivateInvoiceScheduleEscConfirmationModal', ModalActions.Hide);
        this.dirtyFormRegistry.unregister(this);
        this.resolvePendingLeave(true);
    }

    public onEscCancelled(): void {
        this.bootstrapService.manageModal('#reactivateInvoiceScheduleEscConfirmationModal', ModalActions.Hide);
        this.bootstrapService.manageModal('#reactivateInvoiceScheduleModal', ModalActions.Show);
        this.resolvePendingLeave(false);
    }

    //#endregion Modal Management

    //#region Private Helpers

    private reactivateDateRangeValidator() {
        return (group: FormGroup) => {
            this.applyDueAfterIssuanceRule(group);
            this.applyEndDateRules(group);
            return null;
        };
    }

    private applyDueAfterIssuanceRule(group: FormGroup): void {
        const issuedRaw = group.get('issuedDate')?.value;
        const dueRaw = group.get('dueDate')?.value;
        const dueControl = group.get('dueDate');

        if (!issuedRaw || !dueRaw || !dueControl) {
            this.clearControlErrors(dueControl, ['dueBeforeIssuance']);
            return;
        }

        const issuedDate = DateUtilities.parseLocalDate(issuedRaw);
        const dueDate = DateUtilities.parseLocalDate(dueRaw);

        if (dueDate < issuedDate) {
            this.mergeControlError(dueControl, 'dueBeforeIssuance', 'Due date cannot be before the issued date');
        } else {
            this.clearControlErrors(dueControl, ['dueBeforeIssuance']);
        }
    }

    private applyEndDateRules(group: FormGroup): void {
        const endControl = group.get('endDate');
        if (!endControl || endControl.disabled) return;

        const issuedRaw = group.get('issuedDate')?.value;
        const endRaw = endControl.value;
        if (!issuedRaw || !endRaw) {
            this.clearControlErrors(endControl, ['endBeforeIssuance', 'endBeforeMonthEnd']);
            return;
        }

        const issuedDate = DateUtilities.parseLocalDate(issuedRaw);
        const endDate = DateUtilities.parseLocalDate(endRaw);

        if (endDate < issuedDate) {
            this.clearControlErrors(endControl, ['endBeforeMonthEnd']);
            this.mergeControlError(endControl, 'endBeforeIssuance', 'End date cannot be before the issued date');
            return;
        }
        this.clearControlErrors(endControl, ['endBeforeIssuance']);

        // Xander's rule: when the derived sendInvoiceAtEndOfMonth applies (issued-day > 27 and the
        // schedule is not Once), the API rejects an End Date before the issuance month's last day
        // because nothing would ever issue.
        const scheduleFrequency = this.schedule()?.frequency;
        const sendAtEndOfMonth = issuedDate.getDate() > 27 && scheduleFrequency !== ScheduleFrequency.Once;
        if (!sendAtEndOfMonth) {
            this.clearControlErrors(endControl, ['endBeforeMonthEnd']);
            return;
        }

        const lastDay = DateUtilities.lastDayOfMonth(issuedDate);
        if (endDate < lastDay) {
            const message = FormValidatorMessages.ChargeScheduleEndDateBeforeMonthEnd
                .replace('{date}', DateUtilities.format(lastDay));
            this.mergeControlError(endControl, 'endBeforeMonthEnd', message);
        } else {
            this.clearControlErrors(endControl, ['endBeforeMonthEnd']);
        }
    }

    private mergeControlError(control: AbstractControl, key: string, message: string): void {
        control.setErrors({
            ...(control.errors || {}),
            [key]: { message }
        });
    }

    private clearControlErrors(control: AbstractControl | null, keys: string[]): void {
        if (!control?.errors) return;
        const remaining = { ...control.errors };
        keys.forEach(k => delete remaining[k]);
        control.setErrors(Object.keys(remaining).length ? remaining : null);
    }

    private buildRequest(schedule: PropertyChargeScheduleModel): UpdatePropertyChargeScheduleRequest {
        const formValue = this.reactivateForm.getRawValue();
        const issuanceDate = DateUtilities.parseLocalDate(formValue.issuedDate);
        const dueDate = DateUtilities.parseLocalDate(formValue.dueDate);
        const dueDay = dueDate.getDate();
        const isDueEndOfMonth = dueDay > 27;
        const isIssuanceEndOfMonth = issuanceDate.getDate() > 27;

        const monthsDiff = (dueDate.getFullYear() - issuanceDate.getFullYear()) * 12
                         + (dueDate.getMonth() - issuanceDate.getMonth());

        // Send the chosen end date only if the user opted in AND the picker has a value; otherwise
        // omit it so the API clears any past-dated scheduleEnd (Corey's requirement).
        const scheduleEnd = formValue.endDateEnabled && formValue.endDate
            ? formValue.endDate
            : undefined;

        return {
            name: schedule.name,
            description: schedule.description || undefined,
            scheduledAmount: schedule.scheduledAmount,
            memo: schedule.memo || undefined,
            revenueAccount: schedule.revenueAccount.chartOfAccountId,
            assetAccount: schedule.assetAccount.chartOfAccountId,
            activate: true,
            memberPaysServiceFee: schedule.memberPaysServiceFee,
            scheduleFrequency: schedule.frequency,
            // The API rejects a month count on any non-Custom frequency, so don't trust the response to be clean.
            customFrequencyMonths: schedule.frequency === ScheduleFrequency.Custom ? schedule.customFrequencyMonths ?? null : null,
            firstIssuanceDate: formValue.issuedDate,
            sendInvoiceAtEndOfMonth: isIssuanceEndOfMonth && schedule.frequency !== ScheduleFrequency.Once,
            invoiceDueNumberOfMonthsFromIssuance: monthsDiff,
            invoiceDueDayOfMonth: isDueEndOfMonth ? undefined : dueDay,
            invoiceDueAtEndOfMonth: isDueEndOfMonth,
            scheduleEnd,
            properties: {
                useFilter: false,
                filter: null,
                selectedIds: schedule.propertyIds ?? [],
                excludedIds: null,
                note: null
            },
            bankFeeExpenseAccount: schedule.bankFeeExpenseAccount?.chartOfAccountId,
            note: undefined
        };
    }

    private resetForm(): void {
        this.submitSucceeded = false;
        const schedule = this.schedule();
        // Default the End Date opt-in to whatever the schedule already has: if it had one, prefill
        // and enable; otherwise leave the picker cleared and disabled so the user can opt in.
        const existingEndDate = schedule?.scheduleEnd || '';
        const enableEndDate = this.showEndDateControls() && !!existingEndDate;
        this.reactivateForm.reset({
            issuedDate: '',
            dueDate: '',
            endDateEnabled: enableEndDate,
            endDate: enableEndDate ? existingEndDate : ''
        });
        const endDateControl = this.reactivateForm.get('endDate');
        if (endDateControl) {
            if (enableEndDate) {
                endDateControl.enable({ emitEvent: false });
            } else {
                endDateControl.disable({ emitEvent: false });
            }
        }
    }

    //#endregion Private Helpers
}
